import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { DashboardStats } from '../../data/requestDashboard';
import { TODAY_LABEL } from '../../data/requestDashboard';
import { fmtDec, fmtNum, pctChange } from '../../utils/requestDashboard';
export type KpiTarget = 'requests' | 'open' | 'late' | 'sla' | 'survey';
interface DashboardKpisProps {
  stats: DashboardStats;
  periodLabel: Localized;
  onSelect: (target: KpiTarget) => void;
}
type Tone = 'good' | 'bad' | 'neutral';
const TONE_TEXT: Record<Tone, string> = {
  good: 'text-emerald-600 font-medium',
  bad: 'text-red-600 font-medium',
  neutral: 'text-slate-500'
};
function KpiCard({
  label,
  value,
  suffix,
  note,
  tone,
  detail,
  cta,
  onClick
}: {
  label: string;
  value: string;
  suffix?: string;
  note: string;
  tone: Tone;
  detail?: string;
  cta: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-label={`${label} : ${value}${suffix ?? ''}. ${cta}`} className="group text-left border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white hover:border-[#4F46E5] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/40 transition-all" data-id="element-5272" data-mp="263umfiqlasko">
      <div className="flex items-start justify-between gap-2" data-id="element-5273" data-mp="2kts89s11r66p">
        <span className="text-sm font-medium text-slate-500 font-body" data-id="element-5274" data-mp="2pqil86hk1lnw">{label}</span>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#4F46E5] transition-colors shrink-0" data-id="element-5275" data-mp="2tsdvw1ldlbec" />
      </div>
      <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5276" data-mp="2ytjo7yb4iwnc">
        {value}
        {suffix && <span className="text-lg text-slate-400 font-semibold" data-id="element-5277" data-mp="33q0fh1dmdr4n">{suffix}</span>}
      </div>
      <div className={`text-sm font-body ${TONE_TEXT[tone]}`} data-id="element-5278" data-mp="14f905s2cs6im">{note}</div>
      {detail && <div className="text-xs text-slate-400 font-body -mt-1" data-id="element-5279" data-mp="19cin9129s63a">{detail}</div>}
    </button>;
}
function GroupLabel({
  title,
  subtitle
}: {
  title: string;
  subtitle: string;
}) {
  return <div className="sm:col-span-2 flex items-baseline gap-2 text-xs font-body" data-id="element-5280" data-mp="xbiz1kdpyxv6">
      <span className="font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5281" data-mp="1baseghu3pny3">{title}</span>
      <span className="text-slate-400" data-id="element-5282" data-mp="1b8fp3jm0kbb0">{subtitle}</span>
    </div>;
}

/** Six KPIs: four period indicators, then two counts of requests open today. */
export function DashboardKpis({
  stats: s,
  periodLabel,
  onSelect
}: DashboardKpisProps) {
  const {
    t,
    lang
  } = useLanguage();
  const vsPrev = t('vs période précédente', 'vs. previous period');
  const pts = s.onTime - s.onTimePrev;
  const dMedian = Math.round((s.medianDays - s.medianDaysPrev) * 10) / 10;
  const dSat = Math.round((s.satisfaction - s.satisfactionPrev) * 10) / 10;
  const signed = (n: number, digits: string) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${digits}`;
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start" data-id="element-5283" data-mp="1z15zhoe9qmj6">
      <GroupLabel title={t('Sur la période', 'Over the period')} subtitle={periodLabel[lang]} data-id="element-5284" data-mp="2imfwvj0av7au" />
      <KpiCard label={t('Demandes reçues', 'Requests received')} value={fmtNum(s.received, lang)} note={`${pctChange(s.received, s.receivedPrev, lang)} ${vsPrev}`} tone="neutral" cta={t('Voir les demandes', 'View requests')} onClick={() => onSelect('requests')} data-id="element-5285" data-mp="2mnhy25ds1tfg" />
      <KpiCard label={t('Résolues dans les délais', 'Resolved on time')} value={`${s.onTime}`} suffix={lang === 'fr' ? ' %' : '%'} note={`${signed(pts, `${Math.abs(pts)}`)} ${t('points', 'points')} ${vsPrev}`} tone={pts >= 0 ? 'good' : 'bad'} cta={t('Voir le respect des SLA par service', 'View SLA compliance by service')} onClick={() => onSelect('sla')} data-id="element-5286" data-mp="1yxnpw14ekt1j" />
      <KpiCard label={t('Délai médian de résolution', 'Median resolution time')} value={fmtDec(s.medianDays, lang)} suffix={` ${t('j', 'd')}`} note={`${signed(dMedian, fmtDec(Math.abs(dMedian), lang))} ${t('j', 'd')} ${vsPrev}`} tone={dMedian <= 0 ? 'good' : 'bad'} cta={t('Voir le respect des SLA par service', 'View SLA compliance by service')} onClick={() => onSelect('sla')} data-id="element-5287" data-mp="1g0mfg0s2y0q7" />
      <KpiCard label={t('Satisfaction après traitement', 'Satisfaction after handling')} value={fmtDec(s.satisfaction, lang)} suffix=" / 5" note={`${signed(dSat, fmtDec(Math.abs(dSat), lang))} ${vsPrev}`} tone={dSat >= 0 ? 'good' : 'bad'} detail={t(`${fmtNum(s.surveyResponses, 'fr')} réponses au sondage (${s.surveyRate} % des demandes fermées)`, `${fmtNum(s.surveyResponses, 'en')} survey responses (${s.surveyRate}% of closed requests)`)} cta={t('Voir les résultats', 'View results')} onClick={() => onSelect('survey')} data-id="element-5288" data-mp="2eh5trt3l1nf6" />

      <GroupLabel title={t('À ce jour', 'As of today')} subtitle={t(`Dossiers ouverts au ${TODAY_LABEL.fr}`, `Open files as of ${TODAY_LABEL.en}`)} data-id="element-5289" data-mp="3kz2plso0n4cu" />
      <KpiCard label={t('Demandes en cours', 'Open requests')} value={fmtNum(s.open, lang)} note={s.openAtRisk > 0 ? t(`dont ${s.openAtRisk} proches de l’échéance`, `incl. ${s.openAtRisk} close to deadline`) : t('aucune proche de l’échéance', 'none close to deadline')} tone={s.openAtRisk > 0 ? 'neutral' : 'good'} cta={t('Voir les dossiers sur la carte', 'View files on the map')} onClick={() => onSelect('open')} data-id="element-5290" data-mp="3sh96xangquke" />
      <KpiCard label={t('Demandes en retard', 'Late requests')} value={fmtNum(s.late, lang)} note={t(`${s.latePrev} il y a 30 jours`, `${s.latePrev} 30 days ago`)} tone={s.late > 0 ? 'bad' : 'good'} cta={t('Voir les dossiers en retard', 'View late files')} onClick={() => onSelect('late')} data-id="element-5291" data-mp="3jiov8tgbll5o" />
    </div>;
}